import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ChevronUp, ChevronDown, Trophy } from 'lucide-react'
import type { ScoreSet } from '@/services/matchService'

interface PlayerInfo {
  userId?: string
  displayName: string
  avatar?: string
}

interface BadmintonSetScoreProps {
  setIndex: number
  player1: PlayerInfo
  player2: PlayerInfo
  value: ScoreSet
  onChange: (value: ScoreSet) => void
  /** Whether to show "Game X" or just "Score" label */
  showGameLabel?: boolean
  /** Compact mode for use in smaller dialogs */
  compact?: boolean
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

/**
 * Calculate valid loser score range based on winner score
 * 
 * Standard badminton scoring (rally point system):
 * - Games are played to 21 points
 * - You must win by 2 clear points
 * - At 29-29, the next point wins (sudden death)
 * - Hard cap at 30
 * 
 * Valid scores:
 * - 21: loser 0-19
 * - 22-29: loser = winner - 2 (deuce, need 2-point lead)
 * - 30: loser can be 28 (from 28-28) or 29 (sudden death from 29-29)
 */
function getLoserScoreRange(winnerScore: number): { min: number; max: number } {
  if (winnerScore < 21) {
    return { min: 0, max: 19 } // Invalid winner score, but provide defaults
  }
  if (winnerScore === 21) {
    return { min: 0, max: 19 }
  }
  if (winnerScore >= 22 && winnerScore <= 29) {
    // Deuce: loser must be exactly winner - 2
    return { min: winnerScore - 2, max: winnerScore - 2 }
  }
  if (winnerScore === 30) {
    // At 30, loser can be 28 (from 28-28) or 29 (sudden death from 29-29)
    return { min: 28, max: 29 }
  }
  return { min: 0, max: 19 }
}

/**
 * Validate a badminton score follows official rules
 * 
 * Standard badminton scoring:
 * - 21-0 to 21-19: standard win
 * - 22-20 to 29-27: deuce (must win by 2)
 * - 30-28: from 28-28, winner scores 2 consecutive
 * - 30-29: sudden death from 29-29
 */
function isValidBadmintonScore(score1: number, score2: number): boolean {
  const winnerScore = Math.max(score1, score2)
  const loserScore = Math.min(score1, score2)

  if (winnerScore > 30 || loserScore > 30) return false
  if (winnerScore < 21) return false
  if (score1 === score2) return false

  if (winnerScore === 21) {
    return loserScore >= 0 && loserScore <= 19
  } else if (winnerScore >= 22 && winnerScore <= 29) {
    return loserScore === winnerScore - 2
  } else if (winnerScore === 30) {
    // 30-28 (from 28-28) or 30-29 (sudden death) are both valid
    return loserScore === 28 || loserScore === 29
  }
  return false
}

/**
 * Sanitize a potentially invalid score to a valid one
 * 
 * Examples of fixes:
 * - 28-29 → 27-29 (loser must be winner-2 in deuce)
 * - 25-29 → 27-29 (loser must be winner-2)
 * - 27-30 → 28-30 (at 30, loser must be 28 or 29)
 */
function sanitizeScore(score1: number, score2: number): { player1Score: number; player2Score: number } {
  // If already valid, return as-is
  if (isValidBadmintonScore(score1, score2)) {
    return { player1Score: score1, player2Score: score2 }
  }

  // Handle the case where both are 0 (no winner selected yet)
  if (score1 === 0 && score2 === 0) {
    return { player1Score: 0, player2Score: 0 }
  }

  // Determine intended winner (higher score)
  const player1Wins = score1 > score2
  const winnerScore = Math.max(score1, score2)
  const loserScore = Math.min(score1, score2)

  // Clamp winner to valid range (21-30)
  const clampedWinner = Math.max(21, Math.min(30, winnerScore))
  
  // Calculate valid loser score based on winner
  const loserRange = getLoserScoreRange(clampedWinner)
  
  // Clamp loser to valid range, preferring to keep close to original
  let clampedLoser: number
  if (loserScore >= loserRange.min && loserScore <= loserRange.max) {
    clampedLoser = loserScore
  } else if (loserScore < loserRange.min) {
    clampedLoser = loserRange.min
  } else {
    clampedLoser = loserRange.max
  }

  return player1Wins
    ? { player1Score: clampedWinner, player2Score: clampedLoser }
    : { player1Score: clampedLoser, player2Score: clampedWinner }
}

/**
 * BadmintonSetScore - A reusable component for entering badminton game scores
 * 
 * Features:
 * - Click on a player to select them as winner (auto-sets 21)
 * - Increase winner score for deuce situations (22-30)
 * - Loser score automatically follows badminton rules
 * - Visual feedback for winner selection
 */
export function BadmintonSetScore({
  setIndex,
  player1,
  player2,
  value,
  onChange,
  showGameLabel = true,
  compact = false,
}: BadmintonSetScoreProps) {
  // Determine current winner from scores
  const winner: 'player1' | 'player2' | null = 
    value.player1Score > value.player2Score ? 'player1' :
    value.player2Score > value.player1Score ? 'player2' : null

  const winnerScore = winner === 'player1' ? value.player1Score : value.player2Score

  // Handle winner selection
  const selectWinner = (selectedWinner: 'player1' | 'player2') => {
    if (selectedWinner === 'player1') {
      onChange({ player1Score: 21, player2Score: 0 })
    } else {
      onChange({ player1Score: 0, player2Score: 21 })
    }
  }

  // Handle winner score change (for deuce situations)
  const adjustWinnerScore = (delta: number) => {
    if (!winner) return

    const currentWinnerScore = winner === 'player1' ? value.player1Score : value.player2Score
    const currentLoserScore = winner === 'player1' ? value.player2Score : value.player1Score
    const newWinnerScore = Math.max(21, Math.min(30, currentWinnerScore + delta))
    
    // Calculate new loser score based on badminton rules:
    // - At 21: loser can be 0-19 (keep current if valid)
    // - At 22-29: loser MUST be winner - 2 (no choice)
    // - At 30: loser can be 28 or 29 (keep current if valid)
    const loserRange = getLoserScoreRange(newWinnerScore)
    
    let newLoserScore: number
    if (newWinnerScore === 21) {
      // At 21, try to keep current loser if valid, otherwise use 0
      newLoserScore = currentLoserScore >= 0 && currentLoserScore <= 19 ? currentLoserScore : 0
    } else if (newWinnerScore === 30) {
      // At 30, loser can be 28 or 29 - keep current if valid, else use 28
      newLoserScore = (currentLoserScore === 28 || currentLoserScore === 29) ? currentLoserScore : 28
    } else {
      // At deuce (22-29), loser must be exactly winner - 2
      newLoserScore = loserRange.min
    }

    if (winner === 'player1') {
      onChange({ player1Score: newWinnerScore, player2Score: newLoserScore })
    } else {
      onChange({ player1Score: newLoserScore, player2Score: newWinnerScore })
    }
  }

  // Handle loser score change (when winner has 21 or 30)
  const adjustLoserScore = (delta: number) => {
    if (!winner) return
    
    const currentWinnerScore = winner === 'player1' ? value.player1Score : value.player2Score
    const currentLoserScore = winner === 'player1' ? value.player2Score : value.player1Score
    
    let newLoserScore: number
    
    if (currentWinnerScore === 21) {
      // At 21, loser can be 0-19
      newLoserScore = Math.max(0, Math.min(19, currentLoserScore + delta))
    } else if (currentWinnerScore === 30) {
      // At 30, loser can be 28 or 29
      newLoserScore = Math.max(28, Math.min(29, currentLoserScore + delta))
    } else {
      // At deuce (22-29), loser cannot be adjusted
      return
    }

    if (winner === 'player1') {
      onChange({ player1Score: value.player1Score, player2Score: newLoserScore })
    } else {
      onChange({ player1Score: newLoserScore, player2Score: value.player2Score })
    }
  }
  
  // Check if loser score can be adjusted
  const canAdjustLoser = winner && (winnerScore === 21 || winnerScore === 30)

  const avatarSize = compact ? 'h-8 w-8' : 'h-10 w-10'
  const scoreSize = compact ? 'text-xl' : 'text-2xl'

  return (
    <div className="space-y-2">
      {/* Game label */}
      {showGameLabel && (
        <div className="text-xs text-muted-foreground text-center">
          {setIndex === 0 ? 'Game' : `Game ${setIndex + 1}`}
        </div>
      )}

      <div className="flex items-center gap-2">
        {/* Player 1 */}
        <button
          type="button"
          onClick={() => selectWinner('player1')}
          className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
            winner === 'player1'
              ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
              : winner === 'player2'
              ? 'border-transparent bg-muted/30 opacity-60'
              : 'border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50'
          }`}
        >
          <Avatar className={avatarSize}>
            <AvatarImage src={player1.avatar} alt={player1.displayName} />
            <AvatarFallback className="text-xs">{getInitials(player1.displayName)}</AvatarFallback>
          </Avatar>
          <span className="text-xs font-medium truncate max-w-full px-1">
            {player1.displayName.split(' ')[0]}
          </span>
          {winner === 'player1' && (
            <Trophy className="h-3 w-3 text-green-600" />
          )}
          {!winner && (
            <span className="text-[10px] text-muted-foreground">Tap to win</span>
          )}
        </button>

        {/* Score display */}
        <div className="flex flex-col items-center gap-1 min-w-[100px]">
          <div className="flex items-center gap-2">
            {/* Player 1 score */}
            <div className="flex flex-col items-center">
              {/* Up arrow - winner increasing or loser increasing */}
              {winner === 'player1' && value.player1Score >= 21 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustWinnerScore(1)}
                  disabled={value.player1Score >= 30}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
              {winner === 'player2' && canAdjustLoser && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustLoserScore(1)}
                  disabled={winnerScore === 21 ? value.player1Score >= 19 : value.player1Score >= 29}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
              
              <span className={`${scoreSize} font-bold tabular-nums ${
                winner === 'player1' ? 'text-green-600' : 'text-muted-foreground'
              }`}>
                {value.player1Score}
              </span>
              
              {/* Down arrow - winner decreasing or loser decreasing */}
              {winner === 'player1' && value.player1Score >= 21 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustWinnerScore(-1)}
                  disabled={value.player1Score <= 21}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
              {winner === 'player2' && canAdjustLoser && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustLoserScore(-1)}
                  disabled={winnerScore === 21 ? value.player1Score <= 0 : value.player1Score <= 28}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
            </div>

            <span className="text-muted-foreground text-lg">-</span>

            {/* Player 2 score */}
            <div className="flex flex-col items-center">
              {/* Up arrow - winner increasing or loser increasing */}
              {winner === 'player2' && value.player2Score >= 21 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustWinnerScore(1)}
                  disabled={value.player2Score >= 30}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
              {winner === 'player1' && canAdjustLoser && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustLoserScore(1)}
                  disabled={winnerScore === 21 ? value.player2Score >= 19 : value.player2Score >= 29}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
              
              <span className={`${scoreSize} font-bold tabular-nums ${
                winner === 'player2' ? 'text-green-600' : 'text-muted-foreground'
              }`}>
                {value.player2Score}
              </span>
              
              {/* Down arrow - winner decreasing or loser decreasing */}
              {winner === 'player2' && value.player2Score >= 21 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustWinnerScore(-1)}
                  disabled={value.player2Score <= 21}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
              {winner === 'player1' && canAdjustLoser && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => adjustLoserScore(-1)}
                  disabled={winnerScore === 21 ? value.player2Score <= 0 : value.player2Score <= 28}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Deuce indicator */}
          {winner && winnerScore >= 22 && (
            <span className="text-[10px] text-amber-600 font-medium">Deuce</span>
          )}
        </div>

        {/* Player 2 */}
        <button
          type="button"
          onClick={() => selectWinner('player2')}
          className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
            winner === 'player2'
              ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
              : winner === 'player1'
              ? 'border-transparent bg-muted/30 opacity-60'
              : 'border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50'
          }`}
        >
          <Avatar className={avatarSize}>
            <AvatarImage src={player2.avatar} alt={player2.displayName} />
            <AvatarFallback className="text-xs">{getInitials(player2.displayName)}</AvatarFallback>
          </Avatar>
          <span className="text-xs font-medium truncate max-w-full px-1">
            {player2.displayName.split(' ')[0]}
          </span>
          {winner === 'player2' && (
            <Trophy className="h-3 w-3 text-green-600" />
          )}
          {!winner && (
            <span className="text-[10px] text-muted-foreground">Tap to win</span>
          )}
        </button>
      </div>
    </div>
  )
}

/**
 * BadmintonScoreEntry - Multi-set score entry component
 */
interface BadmintonScoreEntryProps {
  player1: PlayerInfo
  player2: PlayerInfo
  sets: ScoreSet[]
  onSetsChange: (sets: ScoreSet[]) => void
  minSets?: number
  maxSets?: number
  setsToWin?: number
}

export function BadmintonScoreEntry({
  player1,
  player2,
  sets,
  onSetsChange,
  minSets = 1,
  maxSets = 1,
  setsToWin = 1,
}: BadmintonScoreEntryProps) {
  // Track the last sanitized value to prevent infinite loops
  const lastSanitizedRef = useRef<string>('')
  
  // Sanitize any invalid scores when they come in (e.g., legacy data or async props)
  useEffect(() => {
    // Create a key for current sets
    const currentKey = JSON.stringify(sets)
    
    // Skip if we just set this value
    if (currentKey === lastSanitizedRef.current) {
      return
    }
    
    let needsUpdate = false
    const sanitizedSets = sets.map(set => {
      // Skip if both are 0 (no winner selected yet)
      if (set.player1Score === 0 && set.player2Score === 0) {
        return set
      }
      if (!isValidBadmintonScore(set.player1Score, set.player2Score)) {
        needsUpdate = true
        return sanitizeScore(set.player1Score, set.player2Score)
      }
      return set
    })
    
    if (needsUpdate) {
      // Track what we're setting to prevent loops
      lastSanitizedRef.current = JSON.stringify(sanitizedSets)
      onSetsChange(sanitizedSets)
    }
  }, [sets, onSetsChange])

  const updateSet = (index: number, value: ScoreSet) => {
    const newSets = [...sets]
    newSets[index] = value
    onSetsChange(newSets)
  }

  const addSet = () => {
    if (sets.length < maxSets) {
      onSetsChange([...sets, { player1Score: 0, player2Score: 0 }])
    }
  }

  const removeSet = (index: number) => {
    if (sets.length > minSets) {
      onSetsChange(sets.filter((_, i) => i !== index))
    }
  }

  // Calculate wins
  const player1Wins = sets.filter(s => s.player1Score > s.player2Score).length
  const player2Wins = sets.filter(s => s.player2Score > s.player1Score).length
  const matchWinner = player1Wins >= setsToWin ? 'player1' : player2Wins >= setsToWin ? 'player2' : null

  return (
    <div className="space-y-4">
      {sets.map((set, index) => (
        <div key={index} className="relative">
          <BadmintonSetScore
            setIndex={index}
            player1={player1}
            player2={player2}
            value={set}
            onChange={(value) => updateSet(index, value)}
            showGameLabel={maxSets > 1}
          />
          {sets.length > minSets && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute -right-2 top-0 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeSet(index)}
            >
              ×
            </Button>
          )}
        </div>
      ))}

      {/* Add set button */}
      {maxSets > 1 && sets.length < maxSets && !matchWinner && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={addSet}
        >
          + Add Game {sets.length + 1}
        </Button>
      )}

      {/* Match winner display */}
      {matchWinner && maxSets > 1 && (
        <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <Trophy className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-700 dark:text-green-400">
            {matchWinner === 'player1' ? player1.displayName : player2.displayName} wins ({player1Wins}-{player2Wins})!
          </span>
        </div>
      )}

      {/* Current score for multi-set */}
      {maxSets > 1 && !matchWinner && (player1Wins > 0 || player2Wins > 0) && (
        <div className="text-center text-sm text-muted-foreground">
          Games: {player1.displayName.split(' ')[0]} {player1Wins} - {player2Wins} {player2.displayName.split(' ')[0]}
        </div>
      )}
    </div>
  )
}
